import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  CreditCard,
  LifeBuoy,
  LogOut,
  Receipt,
  Scale,
  ScrollText,
  Settings,
  Shield,
  User,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { interpolate } from '../content/appLocale';
import LocaleSelector from './LocaleSelector';
import '../styles/studio.css';

export default function UserProfileMenu({ user, copy, onLogout, className }) {
  const pm = copy.profileMenu;
  const freeCredits = user?.free_credits || 0;
  const paidCredits = user?.paid_credits || 0;
  const totalCredits = user?.total_credits ?? user?.credits ?? freeCredits + paidCredits;
  const displayName = (user?.name && String(user.name).trim()) || '';
  const email = user?.email || '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'studio-profile-trigger',
          className
        )}
      >
        <User className="h-4 w-4 shrink-0" aria-hidden />
        <span className="max-w-[140px] truncate sm:max-w-none">{pm.triggerLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="adfini-studio studio-profile-menu"
      >
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            {displayName ? (
              <span className="truncate text-sm font-medium text-[var(--studio-ink)]">{displayName}</span>
            ) : null}
            {email ? (
              <span className="truncate text-xs text-[var(--studio-muted)]">{email}</span>
            ) : (
              <span className="text-xs text-[var(--studio-muted)]">{pm.signedInAs}</span>
            )}
          </div>
        </DropdownMenuLabel>
        <div className="px-2 py-2">
          <div className="text-xs font-medium text-[var(--studio-muted)]">
            {pm.balanceHeading}
          </div>
          <div className="studio-profile-balance">
            {interpolate(pm.totalLine, { total: totalCredits })}
          </div>
          <div className="mt-0.5 text-xs text-[var(--studio-muted)]">
            {interpolate(pm.freePaidLine, { free: freeCredits, paid: paidCredits })}
          </div>
        </div>
        <div className="px-2 py-2" onPointerDown={(e) => e.stopPropagation()}>
          <div className="mb-1.5 text-xs font-medium text-[var(--studio-muted)]">
            {copy.common.localeLabel}
          </div>
          <LocaleSelector variant="menu" />
        </div>
        <DropdownMenuSeparator className="bg-[var(--studio-line)]" />
        <DropdownMenuItem asChild className="studio-profile-item">
          <Link to="/billing" className="flex w-full items-center gap-2">
            <CreditCard className="h-4 w-4" />
            {pm.creditsAndPlans}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="studio-profile-item">
          <Link to="/settings" className="flex w-full items-center gap-2">
            <Settings className="h-4 w-4" />
            {pm.accountSettings}
          </Link>
        </DropdownMenuItem>
        {user?.role === 'admin' ? (
          <DropdownMenuItem asChild className="studio-profile-item">
            <Link to="/admin" className="flex w-full items-center gap-2">
              <Shield className="h-4 w-4" />
              {pm.adminDashboard}
            </Link>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator className="bg-[var(--studio-line)]" />
        <DropdownMenuLabel className="px-2 py-1.5 text-xs font-medium text-[var(--studio-muted)]">
          {pm.legalHeading}
        </DropdownMenuLabel>
        <DropdownMenuItem asChild className="studio-profile-item">
          <Link to="/privacy" className="flex w-full items-center gap-2">
            <ScrollText className="h-4 w-4 opacity-80" />
            {pm.privacyPolicy}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="studio-profile-item">
          <Link to="/terms" className="flex w-full items-center gap-2">
            <Scale className="h-4 w-4 opacity-80" />
            {pm.termsOfService}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="studio-profile-item">
          <Link to="/refund-policy" className="flex w-full items-center gap-2">
            <Receipt className="h-4 w-4 opacity-80" />
            {pm.refundPolicy}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="studio-profile-item">
          <Link to="/help" className="flex w-full items-center gap-2">
            <LifeBuoy className="h-4 w-4 opacity-80" />
            {pm.helpSupport}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[var(--studio-line)]" />
        <DropdownMenuItem
          className="studio-profile-item studio-profile-signout"
          onClick={() => onLogout()}
        >
          <LogOut className="h-4 w-4" />
          {pm.signOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
